'use client'
import { create } from 'zustand';
import { onAuthStateChanged, signOut, getAuth, updateProfile } from 'firebase/auth';
import { collection, getDocs, query, where, onSnapshot, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { getUserFromFirestore } from '../firebase/utils';
import useTaskStore from './useTaskStore';
import { projectCache } from '../services/projectCacheService';

const useAuthStore = create((set, get) => ({
  user: null,
  userDetails: null,
  labels: [],
  projectsData: [], // Full project data including icons
  unRead: false,
  loading: true,
  unsubscribeUnreadListener: null,
  unsubscribeProjectsListener: null, // Add listener for projects
  authProviders: [], // Track all authentication providers

  setUser: async (user) => {
    if (!user) {
      const unsubUnread = get().unsubscribeUnreadListener;
      if (unsubUnread) unsubUnread();
      
      const unsubProjects = get().unsubscribeProjectsListener;
      if (unsubProjects) unsubProjects();

      // Clear project cache when user is set to null
      projectCache.clearAll();

      set(() => ({ 
        user: null, 
        userDetails: null, 
        labels: [], 
        loading: false, 
        unRead: false, 
        unsubscribeUnreadListener: null,
        unsubscribeProjectsListener: null,
        authProviders: []
      }));
      return;
    }
    // Clear cache when switching users to prevent data leakage
    const currentUser = get().user;
    if (currentUser && currentUser.uid !== user.uid) {
      projectCache.clearAll();
    }
    
    // Make the user available right away; the profile document fills in behind it.
    const providers = user.authProviders || [];
    set((s) => ({ user: s.user?.uid === user.uid ? { ...s.user, ...user } : user, authProviders: providers }));
    get().listenToUnreadNotifications(user.uid);
    get().listenToProjects(user.uid); // Add real-time projects listener
    await get().fetchUser(user.uid);
    set(() => ({ loading: false }));
  },
  fetchUser: async (userId) => {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        const userDetails = {
          id: userDocSnap.id,
          ...userDocSnap.data()
        };
        
        // Update auth providers from user details
        const providers = userDetails.authProviders || [];
        set(() => ({ 
          userDetails, 
          authProviders: providers 
        }));
        return userDetails;
      } else {
        console.error("No user document found!");
        set(() => ({ userDetails: null, authProviders: [] }));
        return null;
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      set(() => ({ userDetails: null, authProviders: [] }));
      return null;
    }
  },
    updateUserDetails: async (newName, photoURL = null) => {
    const { user, userDetails } = get();

    // Save original state for smooth rollback on error
    const originalUserDetails = userDetails;

    if (!user || !user.uid) {
      console.error("No authenticated user found");
      return false;
    }

    if (!newName || newName.trim() === '') {
      console.error("Name cannot be empty");
      return false;
    }

    try {
      // Don't set global loading state for smooth autosave
      // set(() => ({ loading: true }));

      // Prepare update data
      const updateData = {
        name: newName.trim()
      };

      // Add photoURL if provided
      if (photoURL !== null) {
        updateData.profileUrl = photoURL;
      }

      // Update in Firestore
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, updateData);

      // Update Firebase Auth displayName
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (currentUser) {
        await updateProfile(currentUser, {
          displayName: newName.trim(),
          ...(photoURL !== null && { photoURL: photoURL })
        });
      }

      // Update local state optimistically
      if (userDetails) {
        const updatedUserDetails = {
          ...userDetails,
          name: newName.trim()
        };

        if (photoURL !== null) {
          updatedUserDetails.profileUrl = photoURL;
        }

        set(() => ({ userDetails: updatedUserDetails }));
      }

      // Refetch user data in background to ensure consistency with Firestore
      // This catches any server-side transformations or race conditions
      // We do this silently without showing loading state for smooth autosave UX
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const freshUserDetails = {
            id: userDocSnap.id,
            ...userDocSnap.data()
          };
          set(() => ({ userDetails: freshUserDetails }));
        }
      } catch (refetchError) {
        // Silently fail - the optimistic update is already in place
        console.warn('Background refetch failed, using optimistic update:', refetchError);
      }

      return true;
    } catch (error) {
      console.error("Error updating user details:", error);

      // Rollback to original state without triggering loading spinner
      // This provides smooth UX instead of jarring fetchUser() call
      set(() => ({ userDetails: originalUserDetails }));

      return false;
    }
  },

  listenToProjects: (userId) => {
    // ✅ Clean up old listener
    const prevUnsub = get().unsubscribeProjectsListener;
    if (prevUnsub) prevUnsub();

    const projectsRef = collection(db, 'users', userId, 'labels');
    let isInitialLoad = true;

    const unsubscribe = onSnapshot(projectsRef, (snapshot) => {
      const allProjects = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort projects: favorites first, then alphabetical
      const sortedProjects = allProjects.sort((a, b) => {
        // Favorites first
        if (a.isFavorite && !b.isFavorite) return -1;
        if (!a.isFavorite && b.isFavorite) return 1;
        // Then alphabetical by label
        return (a.label || '').localeCompare(b.label || '');
      });

      // For labels array, only include non-archived (for backward compatibility)
      // Deduplicate labels at the source to ensure consistency across all components
      const labels = [...new Set(sortedProjects
        .filter(p => p.status !== 'archived') // Use status field instead of archived boolean
        .map(p => p.label))];

      // Store all projects data (including archived), sorted
      set(() => ({ labels, projectsData: sortedProjects }));

      // Mark initial load complete
      if (isInitialLoad) {
        isInitialLoad = false;
      }
    }, (error) => {
      console.error('Error listening to projects:', error);
    });

    // ✅ Store new listener in Zustand
    set(() => ({ unsubscribeProjectsListener: unsubscribe }));
  },

  fetchLabels: async (userId) => {
    try {
      const labelsRef = collection(db, 'users', userId, 'labels');
      // Fetch ALL projects, including archived ones
      const labelDocs = await getDocs(labelsRef);

      // Separate active and archived projects
      const allProjects = labelDocs.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort projects: favorites first, then alphabetical
      const sortedProjects = allProjects.sort((a, b) => {
        // Favorites first
        if (a.isFavorite && !b.isFavorite) return -1;
        if (!a.isFavorite && b.isFavorite) return 1;
        // Then alphabetical by label
        return (a.label || '').localeCompare(b.label || '');
      });

      // For labels array, only include non-archived (for backward compatibility)
      // Deduplicate labels at the source to ensure consistency across all components
      const labels = [...new Set(sortedProjects
        .filter(p => p.status !== 'archived') // Use status field instead of archived boolean
        .map(p => p.label))];

      // Store all projects data (including archived), sorted. Labels load in the background,
      // so this must not touch the global loading flag.
      set(() => ({ labels, projectsData: sortedProjects }));
      return labels;
    } catch (error) {
      console.error("Error fetching labels:", error);
      set(() => ({ loading: false }));
      return [];
    }
  },
  listenToUnreadNotifications: (userId) => {
    // ✅ Clean up old listener
    const prevUnsub = get().unsubscribeUnreadListener;
    if (prevUnsub) prevUnsub();

    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('userId', '==', userId),
      where('delivered', '==', true),
      where('isRead', '==', false)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      set(() => ({ unRead: !snapshot.empty }));
    }, (error) => {
      console.error('Error listening to unread notifications:', error);
    });

    // ✅ Store new listener in Zustand
    set(() => ({ unsubscribeUnreadListener: unsubscribe }));
  },
  setLoading: (loading) => set(() => ({ loading })),

  logout: async () => {
    try {
      await signOut(auth);
      // Clear project cache on logout to prevent data leakage
      projectCache.clearAll();
      // Clear session storage to reset toast display for next login
      sessionStorage.clear();
      
      // Clean up listeners
      const unsubUnread = get().unsubscribeUnreadListener;
      if (unsubUnread) unsubUnread();
      
      const unsubProjects = get().unsubscribeProjectsListener;
      if (unsubProjects) unsubProjects();
      
      set(() => ({ 
        user: null, 
        labels: [], 
        projectsData: [], 
        loading: false,
        unsubscribeUnreadListener: null,
        unsubscribeProjectsListener: null
      }));
      useTaskStore.getState().clearState();
    } catch (error) {
      console.error('Logout error:', error);
    }
  }
})); 
export default useAuthStore;
